interface Modifiers {
  str_weight: number
  dex_weight: number
  sta_weight: number
  int_weight: number
  spi_weight: number
}

interface ComputedStats {
  str: number
  dex: number
  sta: number
  int: number
  spi: number
}

const GRADE_STAT_MULTIPLIERS: Record<number, number> = {
  0: 100, 1: 80, 2: 108, 3: 116, 4: 124, 5: 132,
  6: 140, 7: 150, 8: 160, 9: 170, 10: 185, 11: 200, 12: 210,
}

export const GRADE_COLORS: Record<number, string> = {
  0: '#BA976D', 1: '#949293', 2: '#77b064', 3: '#558fd7',
  4: '#cb72d8', 5: '#d78b06', 6: '#e17853', 7: '#f95252',
  8: '#cf7d5d', 9: '#8fa5ca', 10: '#bf7900', 11: '#c90b0b', 12: '#ae98fe',
}

export const GRADE_NAMES: Record<number, string> = {
  0: 'Basic', 1: 'Crude', 2: 'Grand', 3: 'Rare', 4: 'Arcane',
  5: 'Heroic', 6: 'Unique', 7: 'Celestial', 8: 'Divine', 9: 'Epic',
  10: 'Legendary', 11: 'Mythic', 12: 'Eternal',
}

export const BIND_TEXTS: Record<number, string> = {
  1: 'Tradeable', 2: 'Bind on Pickup', 3: 'Bind on Equip',
  4: 'Bind on Unpack', 5: 'Bind on Pickup', 6: 'Bind on Auction Win',
}

export function gradeStatMultiplier(grade: number): number {
  return GRADE_STAT_MULTIPLIERS[grade] ?? 100
}

function calculateSingleStat(
  temp: number, modCount: number, weight: number,
  totalWeight: number, gradeMul: number, holdableMul: number
): number {
  if (weight === 0) return 0
  const t2 = (modCount * temp * weight) / totalWeight * gradeMul * 0.01 + 0.5
  return Math.floor(t2 * holdableMul * 0.01 + 0.5)
}

export function calculateStats(
  modifiers: Modifiers | null, itemLevel: number, grade: number,
  statMultiplier: number, isWeapon: boolean, coverage: number = 100
): ComputedStats | null {
  if (!modifiers) return null

  const weights = [
    modifiers.str_weight, modifiers.dex_weight, modifiers.sta_weight,
    modifiers.int_weight, modifiers.spi_weight,
  ]
  const nonZero = weights.filter(w => w > 0).length
  const totalWeight = weights.reduce((s, w) => s + w, 0)
  if (totalWeight === 0) return null

  const STAT_VALUE_CONST = 110
  const ITEM_STAT_CONST = 350
  const STAT_CONST = isWeapon ? 10 : 90
  const GRADE_MUL = gradeStatMultiplier(grade)

  let baseMod = 1
  if (nonZero === 1) baseMod = 3
  if (nonZero === 2) baseMod = 1.5

  const mod = Math.pow(baseMod, 1 / (STAT_VALUE_CONST * 0.01))
  const temp = ITEM_STAT_CONST * 0.01 * itemLevel * mod * STAT_CONST * 0.01 * coverage * 0.01

  const calc = (w: number) => calculateSingleStat(temp, nonZero, w, totalWeight, GRADE_MUL, statMultiplier)

  return {
    str: calc(modifiers.str_weight),
    dex: calc(modifiers.dex_weight),
    sta: calc(modifiers.sta_weight),
    int: calc(modifiers.int_weight),
    spi: calc(modifiers.spi_weight),
  }
}

export function evaluateDpsFormula(formula: string, itemLevel: number, grade: number): number {
  if (!formula || formula === '0') return 0
  const gradeMultiplier = gradeStatMultiplier(grade) / 100

  try {
    const processed = formula
      .replace(/item_level/g, itemLevel.toString())
      .replace(/item_grade/g, gradeMultiplier.toString())
      .replace(/(\d+\.?\d*|\([^)]+\))\s*\^\s*(\d+\.?\d*|\([^)]+\))/g, 'Math.pow($1,$2)')

    return new Function(`return ${processed}`)() as number
  } catch {
    return 0
  }
}

export function calculateArmorDefense(itemLevel: number, grade: number, coverage: number): number {
  const baseDefense = Math.floor(Math.pow(itemLevel, 1.1) * 85 + 100)
  const gradeMul = gradeStatMultiplier(grade) / 100
  return Math.floor(baseDefense * gradeMul * (coverage / 100))
}

/** Pick best slave-equipment description for a grade (highest available grade <= target, else lowest). */
function pickSlaveDescription(byGrade: Record<string, string>, effectiveGrade: number): string {
  const grades = Object.keys(byGrade)
    .map(Number)
    .filter((g) => byGrade[String(g)])
    .sort((a, b) => a - b)
  if (!grades.length) return ''
  const atOrBelow = grades.filter((g) => g <= effectiveGrade)
  const best = atOrBelow.length > 0 ? atOrBelow[atOrBelow.length - 1] : grades[0]
  return byGrade[String(best)] ?? ''
}

/** Gear score formula: item_level * item_grade * gear_score_multiplier (with multipliers as percentages) */
export function computeGearScore(
  itemLevel: number,
  grade: number,
  gearScoreMultiplier: number | null | undefined
): number | null {
  if (gearScoreMultiplier == null || gearScoreMultiplier <= 0) return null
  const gradeMult = gradeStatMultiplier(grade) / 100
  return Math.round(itemLevel * gradeMult * (gearScoreMultiplier / 100))
}

export function computeGradeData(itemData: any, grade: number) {
  const effectiveGrade = itemData.fixed_grade >= 0 ? itemData.fixed_grade : grade
  let equipData = null
  let gearScore: number | null = null

  if (itemData.weapon_data) {
    const wd = itemData.weapon_data
    const stats = calculateStats(
      wd.modifiers, itemData.level, effectiveGrade,
      wd.stat_multiplier || 100, true
    )
    gearScore = computeGearScore(
      itemData.level,
      effectiveGrade,
      wd.gear_score_multiplier
    )
    equipData = {
      dps: evaluateDpsFormula(wd.formula_dps, itemData.level, effectiveGrade),
      mdps: evaluateDpsFormula(wd.formula_mdps, itemData.level, effectiveGrade),
      hdps: evaluateDpsFormula(wd.formula_hdps, itemData.level, effectiveGrade),
      stats,
    }
  } else if (itemData.armor_data) {
    const ad = itemData.armor_data
    const stats = calculateStats(
      ad.modifiers, itemData.level, effectiveGrade,
      ad.stat_multiplier || 100, false, ad.coverage || 100
    )
    const baseDefense = calculateArmorDefense(itemData.level, effectiveGrade, ad.coverage || 100)
    gearScore = computeGearScore(
      itemData.level,
      effectiveGrade,
      ad.gear_score_multiplier
    )
    equipData = {
      defense: Math.floor(baseDefense * ((ad.armor_ratio || 100) / 100)),
      magic_defense: Math.floor(baseDefense * ((ad.magic_resistance_ratio || 0) / 100)),
      stats,
    }
  }

  let description = itemData.description ?? ''
  if (itemData.slave_equipment && itemData.slave_descriptions_by_grade) {
    const slaveDesc = pickSlaveDescription(itemData.slave_descriptions_by_grade, effectiveGrade)
    if (slaveDesc) description = slaveDesc
  }

  return {
    ...itemData,
    grade: effectiveGrade,
    description,
    equip_data: equipData,
    gear_score: gearScore,
  }
}

export function formatDescription(text: string): string {
  if (!text) return ''
  return text
    .replace(/#\{detail_\w+\}/g, '')
    .replace(/\|nd;(.*?)(\|r|$)/g, '<span class="desc-blue">$1</span>')
    .replace(/\|nc;(.*?)(\|r|$)/g, '<span class="desc-orange">$1</span>')
    .replace(/\|ni;(.*?)(\|r|$)/g, '<span class="desc-yellow">$1</span>')
    .replace(/\|nn;(.*?)(\|r|$)/g, '<span class="desc-dark-orange">$1</span>')
    .replace(/\|ng;(.*?)(\|r|$)/g, '<span class="desc-green">$1</span>')
    .replace(/\|cFF([0-9A-Fa-f]{6})(.*?)(\|r|$)/g, '<span style="color: #$1">$2</span>')
    .replace(/\|r/g, '')
}

export function formatCurrency(copper: number): string {
  if (!copper || copper === 0) return '0c'
  const gold = Math.floor(copper / 10000)
  const silver = Math.floor((copper % 10000) / 100)
  const rem = copper % 100
  const parts: string[] = []
  if (gold > 0) parts.push(`${gold}g`)
  if (silver > 0) parts.push(`${silver}s`)
  if (rem > 0) parts.push(`${rem}c`)
  return parts.join(' ')
}
